/// <reference lib="dom" />
/**
 * webCollectibleDraw.web.ts
 * Canvas2D draw helper for Star Seeds and Constellation Shards.
 *
 * Draws uncollected collectibles from gameState as animated diamond shapes.
 * Seeds: small gold diamonds. Shards: larger cyan diamonds with inner glow.
 * Both bob gently up and down.
 *
 * Also draws the Collectibles tile layer for checkpoint tiles, skipping
 * seed/shard tile IDs (which are handled via gameState collection tracking).
 *
 * Web-only: this file is `.web.ts` so Metro never bundles it on native.
 *
 * @module webCollectibleDraw
 */

import { gameState } from '../engine/gameState';
import { TILE_SIZE, VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from '../../constants/gameConstants';
import { drawTileLayers } from './webTileDraw';
import { activatedCheckpointIds } from '../levels/CheckpointSystem';
import {
  STAR_SEED_ID,
  CONSTELLATION_SHARD_ID,
  HEART_FRAGMENT_ID,
  CHECKPOINT_INACTIVE_ID,
} from '../levels/tileMapHelpers';
import { tileSourceRect, TILESET_TILE_PX } from './TileRenderer';
import type { TiledMap } from './TileMapTypes';

/** Seed colour — gold (fallback when tileset unavailable). */
const SEED_COLOR = '#f5c842';
/** Shard colour — bright cyan (fallback when tileset unavailable). */
const SHARD_COLOR = '#42d4f5';
/** Heart fragment colour — soft pink (fallback when sprite unavailable). */
const HEART_FRAG_COLOR = '#f5427b';
// DECISION: Derive skip-set from shared tile ID constants to avoid drift.
const SKIP_COLLECTIBLE_TILES: ReadonlySet<number> = new Set([
  STAR_SEED_ID, CONSTELLATION_SHARD_ID, HEART_FRAGMENT_ID, CHECKPOINT_INACTIVE_ID,
]);
/** Checkpoint tile ID in the Tiled collectibles layer. */
const CHECKPOINT_TILE_ID = CHECKPOINT_INACTIVE_ID;
/** Collectibles tile layer name. */
const COLLECTIBLE_LAYER = 'Collectibles';

/**
 * Draw uncollected Star Seeds, Constellation Shards, and Heart Fragments
 * from gameState, plus checkpoint tiles from the Collectibles tile layer.
 *
 * @param ctx     - Canvas 2D rendering context.
 * @param camX    - Camera X offset in world space.
 * @param camY    - Camera Y offset in world space.
 * @param timeMs  - Current timestamp in ms (for bob animation).
 * @param tilesetImg - Tileset image for checkpoint tile rendering.
 * @param tiledMap   - Tiled map data (may be null if assets not loaded).
 * @param starSeedImg - PixelLab star seed sprite (48×48).
 * @param shardImg - PixelLab constellation shard sprite (48×48).
 * @param heartImg - PixelLab heart fragment sprite (48×48), or null if unavailable.
 * @param cpActiveImg - PixelLab checkpoint active sprite (48×48).
 * @param cpInactiveImg - PixelLab checkpoint inactive sprite (48×48).
 */
export function drawCollectibles(
  ctx: CanvasRenderingContext2D,
  camX: number,
  camY: number,
  timeMs: number,
  tilesetImg: HTMLImageElement | null,
  tiledMap: TiledMap | null,
  starSeedImg?: HTMLImageElement | null,
  shardImg?: HTMLImageElement | null,
  heartImg?: HTMLImageElement | null,
  cpActiveImg?: HTMLImageElement | null,
  cpInactiveImg?: HTMLImageElement | null,
): void {
  const { collectibles } = gameState;
  const bobOffset = Math.sin(timeMs / 400) * 2; // gentle 2px bob
  for (let i = 0; i < collectibles.length; i++) {
    const c = collectibles[i];
    if (c.collected) continue;
    const dx = Math.round(c.x - camX);
    const dy = Math.round(c.y - camY + bobOffset);
    // Cull off-screen
    if (dx + TILE_SIZE < 0 || dx > VIEWPORT_WIDTH || dy + TILE_SIZE < 0 || dy > VIEWPORT_HEIGHT) continue;

    // DECISION: Use PixelLab 48×48 sprites when available, tileset tiles as
    // secondary fallback, procedural diamonds as last resort.
    const isSeed = c.kind === 'seed';
    const isHeartFrag = c.kind === 'heart_fragment';
    const spriteImg = isHeartFrag ? heartImg : (isSeed ? starSeedImg : shardImg);
    if (spriteImg) {
      // DECISION: Crop bottom 8px — PixelLab bakes a text label into the
      // bottom of each 48×48 sprite. Source rect: top 40px only.
      const sw = spriteImg.naturalWidth;
      const sh = spriteImg.naturalWidth * (40 / 48);
      ctx.drawImage(spriteImg, 0, 0, sw, sh, dx, dy, TILE_SIZE, TILE_SIZE);
    } else {
      const tileId = isHeartFrag ? HEART_FRAGMENT_ID : (isSeed ? STAR_SEED_ID : CONSTELLATION_SHARD_ID);
      const src = tilesetImg ? tileSourceRect(tileId) : null;
      if (tilesetImg && src) {
        ctx.drawImage(
          tilesetImg,
          src.sx, src.sy, TILESET_TILE_PX, TILESET_TILE_PX,
          dx, dy, TILE_SIZE, TILE_SIZE,
        );
      } else {
        // Fallback procedural shape when tileset not loaded
        const cx = dx + TILE_SIZE / 2;
        const cy = dy + TILE_SIZE / 2;
        ctx.fillStyle = isHeartFrag ? HEART_FRAG_COLOR : (isSeed ? SEED_COLOR : SHARD_COLOR);
        ctx.beginPath();
        ctx.moveTo(cx, cy - 6);
        ctx.lineTo(cx + 5, cy);
        ctx.lineTo(cx, cy + 6);
        ctx.lineTo(cx - 5, cy);
        ctx.closePath();
        ctx.fill();
      }
    }
  }
  // Draw checkpoint tiles from the Collectibles layer via the tileset.
  // DECISION: Skip seed/shard/checkpoint tile IDs since those are now drawn
  // custom from gameState / checkpoint tracking. Tile layer is only for
  // any future non-tracked tile types.
  if (tilesetImg && tiledMap) {
    drawTileLayers(ctx, tilesetImg, tiledMap, camX, camY, [COLLECTIBLE_LAYER], SKIP_COLLECTIBLE_TILES);
  }
  // Draw checkpoints with activation visual feedback.
  if (tiledMap) {
    drawCheckpoints(ctx, tiledMap, camX, camY, timeMs, cpActiveImg, cpInactiveImg);
  }
}

// ── Checkpoint drawing ────────────────────────────────────────────────────────

/**
 * Draw checkpoints from the Collectibles tile layer as custom flag posts.
 * Inactive: small grey flag. Activated: enlarged golden beacon with glow.
 *
 * @param ctx     - Canvas 2D rendering context.
 * @param map     - Tiled map data.
 * @param camX    - Camera X offset in world space.
 * @param camY    - Camera Y offset in world space.
 * @param timeMs  - Current timestamp in ms (for glow pulse animation).
 * @param cpActiveImg - PixelLab active checkpoint sprite (48×48).
 * @param cpInactiveImg - PixelLab inactive checkpoint sprite (48×48).
 */
function drawCheckpoints(
  ctx: CanvasRenderingContext2D,
  map: TiledMap,
  camX: number,
  camY: number,
  timeMs: number,
  cpActiveImg?: HTMLImageElement | null,
  cpInactiveImg?: HTMLImageElement | null,
): void {
  const layer = map.layers.find(
    l => l.type === 'tilelayer' && l.name === 'Collectibles',
  );
  if (!layer || !('data' in layer)) return;
  const data = layer.data as number[];
  const mapW = map.width;

  for (let i = 0; i < data.length; i++) {
    if (data[i] !== CHECKPOINT_TILE_ID) continue;
    const col = i % mapW;
    const row = Math.floor(i / mapW);
    const wx = col * TILE_SIZE;
    const wy = row * TILE_SIZE;
    const sx = wx - camX;
    const sy = wy - camY;
    // Cull off-screen (generous margin for glow)
    if (sx + 40 < 0 || sx - 20 > VIEWPORT_WIDTH || sy + 40 < 0 || sy - 20 > VIEWPORT_HEIGHT) continue;

    const cpId = `cp-${col}-${row}`;
    const activated = activatedCheckpointIds.has(cpId);

    if (activated) {
      drawActivatedCheckpoint(ctx, sx, sy, timeMs, cpActiveImg);
    } else {
      drawInactiveCheckpoint(ctx, sx, sy, cpInactiveImg);
    }
  }
}

/** Draw an inactive checkpoint — PixelLab sprite or fallback grey flag on a thin pole. */
function drawInactiveCheckpoint(
  ctx: CanvasRenderingContext2D,
  sx: number,
  sy: number,
  cpInactiveImg?: HTMLImageElement | null,
): void {
  if (cpInactiveImg) {
    // DECISION: Crop left 6px (artifact at x=3 + buffer) + bottom 8px (label).
    // Draw at 2× size, pushed down 4px for ground contact. Keep pole visible.
    const drawSz = TILE_SIZE * 2;
    const srcX = 6;
    const srcW = cpInactiveImg.naturalWidth - srcX - 2;
    const srcH = cpInactiveImg.naturalWidth * (40 / 48);
    ctx.drawImage(cpInactiveImg, srcX, 0, srcW, srcH, sx - TILE_SIZE / 2, sy + TILE_SIZE - drawSz + 4, drawSz, drawSz);
    return;
  }
  const cx = sx + TILE_SIZE / 2;
  // Pole
  ctx.strokeStyle = '#888';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx, sy + TILE_SIZE);
  ctx.lineTo(cx, sy + 2);
  ctx.stroke();
  // Flag triangle
  ctx.fillStyle = '#999';
  ctx.beginPath();
  ctx.moveTo(cx, sy + 2);
  ctx.lineTo(cx + 8, sy + 5);
  ctx.lineTo(cx, sy + 8);
  ctx.closePath();
  ctx.fill();
}

/** Draw an activated checkpoint — PixelLab sprite or fallback tall golden pole with glowing beacon. */
function drawActivatedCheckpoint(
  ctx: CanvasRenderingContext2D,
  sx: number,
  sy: number,
  timeMs: number,
  cpActiveImg?: HTMLImageElement | null,
): void {
  if (cpActiveImg) {
    // Still draw the glow pulse behind the sprite for visual pop
    const pulse = 0.6 + 0.4 * Math.sin(timeMs / 300);
    const glowR = 14 + pulse * 6;
    const cx = sx + TILE_SIZE / 2;
    const glow = ctx.createRadialGradient(cx, sy - 2, 2, cx, sy - 2, glowR);
    glow.addColorStop(0, `rgba(245,200,66,${(0.6 * pulse).toFixed(2)})`);
    glow.addColorStop(0.5, `rgba(245,200,66,${(0.2 * pulse).toFixed(2)})`);
    glow.addColorStop(1, 'rgba(245,200,66,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(cx - glowR, sy - 2 - glowR, glowR * 2, glowR * 2);
    // DECISION: Crop left 2px + right 8px (artifact at x=44) + bottom 8px (label).
    // Push down 4px for ground contact. Keeps pole proportional to inactive.
    const drawSz = TILE_SIZE * 2;
    const srcX = 2;
    const srcW = cpActiveImg.naturalWidth - srcX - 8;
    const srcH = cpActiveImg.naturalWidth * (40 / 48);
    ctx.drawImage(cpActiveImg, srcX, 0, srcW, srcH, sx - TILE_SIZE / 2, sy + TILE_SIZE - drawSz + 4, drawSz, drawSz);
    return;
  }
  const cx = sx + TILE_SIZE / 2;
  const pulse = 0.6 + 0.4 * Math.sin(timeMs / 300);
  const glowR = 14 + pulse * 6;

  // Beacon glow (radial gradient behind the pole)
  const glow = ctx.createRadialGradient(cx, sy - 2, 2, cx, sy - 2, glowR);
  glow.addColorStop(0, `rgba(245,200,66,${(0.6 * pulse).toFixed(2)})`);
  glow.addColorStop(0.5, `rgba(245,200,66,${(0.2 * pulse).toFixed(2)})`);
  glow.addColorStop(1, 'rgba(245,200,66,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(cx - glowR, sy - 2 - glowR, glowR * 2, glowR * 2);

  // Taller golden pole
  ctx.strokeStyle = '#f5c842';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(cx, sy + TILE_SIZE);
  ctx.lineTo(cx, sy - 4);
  ctx.stroke();

  // Bright flag triangle (larger)
  ctx.fillStyle = '#f5c842';
  ctx.beginPath();
  ctx.moveTo(cx, sy - 4);
  ctx.lineTo(cx + 12, sy + 1);
  ctx.lineTo(cx, sy + 6);
  ctx.closePath();
  ctx.fill();

  // Beacon light at the top
  ctx.fillStyle = '#fff4c0';
  ctx.beginPath();
  ctx.arc(cx, sy - 4, 3, 0, Math.PI * 2);
  ctx.fill();
}
